import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, catchError, forkJoin, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { getShiftPatternLabel } from 'src/app/enums/shiftTypeValue.enum';
import {
  SegOrgLevelHistoryField,
  SegOrgLevelHistoryRow,
  SegOrgLevelHistoryScopeOption,
  SegOrgLevelRecordHistory,
} from 'src/app/models/segOrgLevel.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { SegOrgLevelService } from 'src/app/services/seg-org-level.service';
import { ShiftService } from 'src/app/services/shift.service';
import { ToastService } from 'src/app/services/toast.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { GridModel } from 'src/app/shared/grid/grid.model';
import { trackById } from 'src/app/shared/utils/track-by.util';
import { getHistoryChangedFields } from 'src/app/_helpers/history-changed-fields.helper';
import { getHistoryDateTimeDisplay, HISTORY_PAGE_SIZE, historyPageRange } from 'src/app/_helpers/history-popup.helper';

const CHANGE_COLUMN = 'change';

type HistoryFieldValues = Pick<SegOrgLevelRecordHistory, SegOrgLevelHistoryField>;

interface HistoryQuery {
  scope: string | null;
  page: number;
}

const HISTORY_FIELDS: SegOrgLevelHistoryField[] = [
  nameOf<HistoryFieldValues>(x => x.shiftId),
  nameOf<HistoryFieldValues>(x => x.shiftLengthHours),
  nameOf<HistoryFieldValues>(x => x.avgWeeklyHours),
  nameOf<HistoryFieldValues>(x => x.shiftType),
  nameOf<HistoryFieldValues>(x => x.numberOfWorkers),
  nameOf<HistoryFieldValues>(x => x.numberOfContractors),
];

@Component({
  standalone: false,
  selector: 'app-seg-org-level-history',
  templateUrl: './seg-org-level-history.component.html',
  styleUrl: './seg-org-level-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegOrgLevelHistoryComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) segId!: string;
  @Input() initialScopeEntityId: string | null = null;

  readonly displayedColumns: string[] = [
    CHANGE_COLUMN,
    nameOf<SegOrgLevelHistoryRow>(x => x.organizationLevelName),
    ...HISTORY_FIELDS,
    nameOf<SegOrgLevelHistoryRow>(x => x.dateUpdated),
    nameOf<SegOrgLevelHistoryRow>(x => x.updatedByUserName),
  ];
  readonly trackByOptionId = trackById;
  readonly pageSize = HISTORY_PAGE_SIZE;

  rows: SegOrgLevelHistoryRow[] = [];
  scopeOptions: SegOrgLevelHistoryScopeOption[] = [];
  scopeEntityId: string | null = null;
  totalCount: number | null = null;
  pageIndex = 0;
  rangeStart = 0;
  rangeEnd = 0;
  isLoading = true;

  private readonly query$ = new BehaviorSubject<HistoryQuery>({ scope: null, page: 0 });
  private readonly destroy$ = new Subject<void>();
  private activeEntityIds = new Set<string>();
  private shiftNameById = new Map<string, string>();
  private unscopedHistory: GridModel<SegOrgLevelRecordHistory> | null = null;

  constructor(
    private segOrgLevelService: SegOrgLevelService,
    private shiftService: ShiftService,
    private dateFormatService: DateFormatService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.scopeEntityId = this.initialScopeEntityId;
    this.query$.next({ scope: this.scopeEntityId, page: 0 });

    // Removal writes no history row (soft delete is excluded from the history pipeline), so a
    // removed association is recognised by its entity no longer being among the SEG's active
    // associations. The unscoped query is used so a link outside the flyout's site scope is not
    // mistaken for a removal.
    forkJoin({
      associations: this.segOrgLevelService.getAssociations(this.segId),
      shifts: this.shiftService.getAll(),
      history: this.segOrgLevelService.getHistory(this.segId),
    }).pipe(
      tap(({ associations, shifts, history }) => {
        this.activeEntityIds = new Set(associations.map((association) => association.id).filter((id): id is string => !!id));
        this.shiftNameById = new Map(shifts.map((shift) => [String(shift.id), String(shift.name)]));
        this.unscopedHistory = history;
        this.scopeOptions = this.buildScopeOptions(history?.data ?? []);
      }),
      switchMap(() => this.query$),
      tap(() => this.setLoading(true)),
      switchMap((query) => this.loadPage$(query)),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (response) => this.applyResponse(response),
      error: (error) => {
        this.toastService.showError(error);
        this.applyResponse(null);
      },
    });
  }

  // The tab host normally re-creates this component per visit, but if it is kept alive a later
  // per-row entry point arrives as an input change and must re-scope the already loaded view.
  ngOnChanges(changes: SimpleChanges): void {
    const scopeChange = changes[nameOf<SegOrgLevelHistoryComponent>(x => x.initialScopeEntityId)];
    if (!scopeChange || scopeChange.firstChange || this.initialScopeEntityId === this.scopeEntityId) {
      return;
    }
    this.onScopeChange(this.initialScopeEntityId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onScopeChange(entityId: string | null): void {
    this.scopeEntityId = entityId;
    this.pageIndex = 0;
    this.query$.next({ scope: entityId, page: 0 });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.query$.next({ scope: this.scopeEntityId, page: event.pageIndex });
  }

  // The unscoped first page is already loaded (it also seeds the filter options), so only other pages and scopes are fetched.
  private loadPage$({ scope, page }: HistoryQuery): Observable<GridModel<SegOrgLevelRecordHistory> | null> {
    if (!scope && page === 0) {
      return of(this.unscopedHistory);
    }
    return this.segOrgLevelService.getHistory(this.segId, scope, page).pipe(
      catchError((error) => {
        this.toastService.showError(error);
        return of(null);
      }),
    );
  }

  private applyResponse(response: GridModel<SegOrgLevelRecordHistory> | null): void {
    this.rows = (response?.data ?? []).map((history) => this.toRow(history));
    this.totalCount = response ? response.count : null;
    ({ start: this.rangeStart, end: this.rangeEnd } = historyPageRange(this.pageIndex, this.rows.length));
    this.ensureScopeOption(this.rows);
    this.setLoading(false);
  }

  // A per-row entry point can target an association whose history is older than the unscoped
  // first page; derive its option from the scoped result so the filter can still show it.
  private ensureScopeOption(rows: SegOrgLevelHistoryRow[]): void {
    const scope = this.scopeEntityId;
    if (!scope || !rows.length || this.scopeOptions.some((option) => option.id === scope)) {
      return;
    }
    this.scopeOptions = this.buildScopeOptions([...this.unscopedHistory?.data ?? [], ...rows]);
  }

  private buildScopeOptions(records: SegOrgLevelRecordHistory[]): SegOrgLevelHistoryScopeOption[] {
    const optionsById = new Map<string, SegOrgLevelHistoryScopeOption>();
    records.forEach((record) => {
      if (!optionsById.has(record.entityId)) {
        optionsById.set(record.entityId, {
          id: record.entityId,
          name: record.organizationLevelName ?? '-',
          isRemoved: !this.activeEntityIds.has(record.entityId),
        });
      }
    });
    return Array.from(optionsById.values()).sort((left, right) => left.name.localeCompare(right.name));
  }

  private toRow(history: SegOrgLevelRecordHistory): SegOrgLevelHistoryRow {
    const changedFields = getHistoryChangedFields(history);
    const changedLower = new Set(changedFields.map((field) => field.toLowerCase()));
    const changed: Partial<Record<SegOrgLevelHistoryField, boolean>> = {};
    HISTORY_FIELDS.forEach((field) => {
      changed[field] = changedLower.has(field.toLowerCase());
    });

    return {
      ...history,
      changed,
      isInitial: changedFields.length === 0,
      isRemoved: !this.activeEntityIds.has(history.entityId),
      shiftName: history.shiftId ? this.shiftNameById.get(String(history.shiftId)) : undefined,
      shiftTypeLabel: getShiftPatternLabel(history.shiftType),
      dateUpdatedDisplay: getHistoryDateTimeDisplay(history.dateUpdated, this.dateFormatService),
    };
  }

  private setLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
    this.cdr.markForCheck();
  }
}
